import type { z } from 'zod';

import { NO_POS, type SourcePos } from './ast.ts';
import { builtins, builtinVerbs } from './builtins.ts';
import { colNodeLabels } from './column-ref.ts';
import { defaultEvalCtx, type EvalContext } from './EvalContext.ts';
import { createRootEnv, type Env, type EvalResult, resolveSymbolWithSessionFallthrough } from './env.ts';
import {
  AggregateExprError,
  argRefFromZodPath,
  EmptyScriptError,
  ExprError,
  OpExecutionError,
  parseOpCall,
  ShapeError,
  UnknownOpError,
  UnknownVerbError,
} from './errors.ts';
import { materialize } from './materialize.ts';
import { type AstInput, astShape, nodePos, normalize, type OpCall, opCallPos, type Spans } from './normalize.ts';
import {
  type ColumnRefInArg,
  createOpTable,
  createVerbTable,
  type NameTable,
  type OpMeta,
  opMeta,
  pipelineVerbs,
  resolveOpCall,
  type SpecialCtx,
  type SpecialVerbCtx,
  type VerbMeta,
} from './op-meta.ts';
import { parse } from './parser.ts';
import { elemPath, joinPath, kwargPath, paramPath } from './path.ts';
import { resolveSetRefsInPredicate } from './predicate-refs.ts';
import { compileExpr } from './runtime/arquero.ts';
import { typeCheckScript } from './schema/check.ts';
import { runPropagateSchema } from './schema/infer.ts';
import { nestedDatasetCol, schemaFromEntries } from './schema/types.ts';
import { Dataset, expectMaterialized, isLazy, LazyDataset, MaterializedDataset, type Value } from './value.ts';

/** Default op table — all builtin schemas. Consumers extend via `createOpTable`. */
export const defaultOpTable: NameTable = createOpTable([...builtins]);

/** Default verb table — all builtin pipeline-verb schemas. */
export const defaultVerbTable: NameTable = createVerbTable([...builtinVerbs]);

/**
 * Per-evaluation invariants bundled into a single value, threaded
 * through every internal helper (`evalNode`, `evalOpCall`,
 * `evalVerbCall`, `dispatchCall`, `resolveEagerArgs`). Replaces the
 * parallel `(spans, ops, verbs)` parameter list that would
 * otherwise propagate through six signatures; adding `evalCtx`
 * made the SSOT case for bundling (mirrors `BaseSpecialCtx` /
 * `BaseOpMeta` in `op-meta.ts`).
 *
 * Public entry points (`evalExpr`, `evalScript`) keep their
 * argument-style ergonomics; internals receive the bundle.
 */
export type EvalState = {
  readonly spans: Spans;
  readonly ops: NameTable;
  readonly verbs: NameTable;
  readonly evalCtx: EvalContext;
  /**
   * The OUTER column name currently being lifted per-cell, set by
   * `liftPerCell` on the recursive `evalVerbCall` so the re-entered
   * verb `apply` / `propagateSchema` detects the per-cell form and
   * names per-axis outputs. `undefined` at the top level and for
   * row-level dispatch (PHASE_4_PREP.md D11.1).
   */
  readonly liftColumn?: string;
  /**
   * The binding name the enclosing `(transform <name> …)` is sourced
   * from, when its first positional is a bare symbol. Set by the
   * `evalVerb` closure (the transform→verb boundary) and threaded
   * untouched through `liftPerCell` / recursive dispatch via the
   * existing `{ ...state }` spread — same propagation pattern as
   * `liftColumn`. Read by the `filter` cross-dataset set-ref seam to
   * tell a *self*-reference (`(col <name> …)` where `<name>` IS the
   * transformed dataset → per-row accessor) apart from a genuine
   * *foreign* binding ref. `transform.apply` resolves the source
   * symbol to a value and discards it, so the literal name is the
   * only reliable self/foreign signal (no Dataset-identity link).
   */
  readonly sourceBindingName?: string;
};

/**
 * Parse an s-expression script and evaluate it against the given op + verb
 * tables. Returns the value of the last top-level form. Bindings introduced
 * by `dataset` flow into subsequent forms. The `ctx` defaults to a fresh
 * in-memory `EvalContext` — production callers supply one constructed from
 * React state (via `useEvalExpr()`) so session bindings and memoized I/O
 * persist across calls.
 */
export const evalExpr = async (
  src: string,
  ctx: EvalContext = defaultEvalCtx(),
  ops: NameTable = defaultOpTable,
  verbs: NameTable = defaultVerbTable,
): Promise<Value> => {
  const { ast, spans } = normalize(parse(src));
  return evalScript(ast, spans, ctx, ops, verbs);
};

/**
 * Evaluate a pre-normalized script (JS-object AST plus spans sidecar).
 * Direct callers can construct `ast` programmatically; `spans` may be empty
 * when the input has no source attribution.
 *
 * Before evaluation, the script is fed through the pre-evaluation
 * type-check pass (§5.8 / `schema/check.ts`). Any collected
 * diagnostics short-circuit evaluation — they're wrapped in
 * `AggregateExprError` and thrown before any op's `apply` runs, so
 * static type errors surface in one LLM round-trip without paying
 * for I/O.
 *
 * `ctx` defaults to a fresh in-memory `EvalContext` so test callers
 * stay terse; production callers pass an explicit one.
 */
export const evalScript = async (
  ast: readonly AstInput[],
  spans: Spans,
  ctx: EvalContext = defaultEvalCtx(),
  ops: NameTable = defaultOpTable,
  verbs: NameTable = defaultVerbTable,
  initialEnv?: Env,
): Promise<Value> => {
  if (ast.length === 0) {
    throw new EmptyScriptError();
  }
  const typeErrors = typeCheckScript(ast, ops, verbs, spans);
  if (typeErrors.length > 0) {
    throwErrors(typeErrors);
  }
  const opNames = new Set(ops.keys());
  const startEnv = initialEnv ?? createRootEnv(opNames);
  const state: EvalState = { spans, ops, verbs, evalCtx: ctx };
  const seq = await ast.reduce(
    async (acc, node, i) => {
      const prev = await acc;
      return evalNode(node, prev.env, elemPath('', i), state);
    },
    Promise.resolve<EvalResult>({ value: null, env: startEnv }),
  );
  return seq.value;
};

/**
 * Recursive evaluation of a single AST node. Dispatch via `astShape`:
 *
 * - Primitives / Date / Dataset → return as-is.
 * - Vectors → recursively evaluate each element; produce an array Value.
 * - `{ $sym }` → lookup in env; unbound → `UnboundSymbolError`.
 * - `{ name, ... }` → op call; validate against schema; dispatch eager/special.
 * - Anything else → `ShapeError`.
 */
export const evalNode = async (node: AstInput, env: Env, path: string, state: EvalState): Promise<EvalResult> => {
  const shape = astShape(node);
  switch (shape.kind) {
    case 'primitive':
    case 'date':
    case 'dataset':
      return { value: shape.value, env };
    case 'vector': {
      const settled = await Promise.allSettled(
        shape.elements.map((el, i) => evalNode(el, env, elemPath(path, i), state).then((r) => r.value)),
      );
      const { ok, errors } = partitionSettled(settled);
      if (errors.length > 0) {
        throwErrors(errors);
      }
      return { value: ok, env };
    }
    case 'symbolRef': {
      // Env → Session fallthrough (PHASE_4_PREP.md D8); see
      // `resolveSymbolWithSessionFallthrough` for the lookup semantics.
      const value = resolveSymbolWithSessionFallthrough(
        env,
        state.evalCtx.session,
        shape.sym,
        nodePos(state.spans, path),
      );
      return { value, env };
    }
    case 'opCall':
      return evalOpCall(shape.call, env, path, state);
    case 'invalid':
      throw new ShapeError('expected an op call, vector, symbol reference, or literal', nodePos(state.spans, path));
  }
};

/**
 * Shared dispatch contract for the two registries (top-level ops via
 * `opMeta`; pipeline verbs via `pipelineVerbs`). Each registry supplies its
 * lookup table, meta map, not-found error factory, special-form context
 * builder, and adapters for the two apply call shapes. `dispatchCall` then
 * runs the shared 5-step dispatch: lookup → meta-get → eager-or-special
 * branch → apply → wrap non-`ExprError` throws as `OpExecutionError`.
 */
type DispatchSpec<MetaType, ResultType, SpecialCtxType> = {
  readonly table: NameTable;
  // Structural shape — both `Map` and `z.registry` satisfy it via `.get`.
  readonly registry: { readonly get: (schema: z.ZodType) => MetaType | undefined };
  readonly metaLabel: string;
  readonly notFound: new (name: string, available: readonly string[], pos: SourcePos) => ExprError;
  readonly buildSpecialCtx: () => SpecialCtxType;
  readonly callEager: (
    meta: Extract<MetaType, { evaluation: 'eager' }>,
    parsed: unknown,
  ) => Promise<ResultType> | ResultType;
  readonly callSpecial: (
    meta: Extract<MetaType, { evaluation: 'special' }>,
    parsedData: unknown,
    ctx: SpecialCtxType,
  ) => Promise<ResultType> | ResultType;
};

const dispatchCall = async <MetaType, ResultType, SpecialCtxType>(
  call: OpCall,
  env: Env,
  path: string,
  state: EvalState,
  spec: DispatchSpec<MetaType, ResultType, SpecialCtxType>,
): Promise<ResultType> => {
  const { schema, meta } = resolveOpCall(
    call.name,
    spec.table,
    spec.registry,
    opCallPos(state.spans, path),
    spec.metaLabel,
    spec.notFound,
  );
  try {
    if ((meta as { evaluation: 'eager' | 'special' }).evaluation === 'special') {
      const parsed = parseOpCall(schema, call);
      if (!parsed.success) {
        throwErrors(zodIssuesToErrors(parsed.error, path, state.spans));
      }
      return await spec.callSpecial(
        meta as Extract<MetaType, { evaluation: 'special' }>,
        parsed.data,
        spec.buildSpecialCtx(),
      );
    }
    const parsed = await resolveEagerArgs(call, schema, env, path, state);
    return await spec.callEager(meta as Extract<MetaType, { evaluation: 'eager' }>, parsed);
  } catch (e) {
    if (e instanceof ExprError) {
      throw e;
    }
    throw new OpExecutionError(call.name, e instanceof Error ? e.message : String(e), opCallPos(state.spans, path));
  }
};

const evalOpCall = (call: OpCall, env: Env, path: string, state: EvalState): Promise<EvalResult> =>
  dispatchCall<OpMeta, EvalResult, SpecialCtx>(call, env, path, state, {
    table: state.ops,
    registry: opMeta,
    metaLabel: 'op',
    notFound: UnknownOpError,
    buildSpecialCtx: () => ({
      env,
      path,
      spans: state.spans,
      evalCtx: state.evalCtx,
      ops: state.ops,
      verbs: state.verbs,
      evalNode: (n, e, p) => evalNode(n, e, p, state),
      evalVerb: (vc, ds, vp, src) =>
        evalVerbCall(vc, ds, env, vp, src === undefined ? state : { ...state, sourceBindingName: src }),
      // Bind the materializer's required `ops`/`verbs` from the surrounding
      // script's tables so plan-replay dispatches against the same
      // registries the original evaluation used. Routing through a callback
      // (rather than a direct import in `builtins.ts`) keeps the import
      // graph acyclic — `builtins.ts` never depends on `materialize.ts`.
      materialize: (ds) => materialize(ds, state.evalCtx, state.ops, state.verbs),
    }),
    // `parsed`/`data` were just schema-validated; cast through `never` so
    // they pass the `$output` (unique-symbol) parameter type the registry
    // declares.  Returns are widened to the engine's runtime contract
    // (Value, EvalResult); we trust the author's annotation.
    callEager: async (meta, parsed) => ({ value: (await meta.apply(parsed as never)) as Value, env }),
    callSpecial: (meta, data, ctx) => meta.apply(data as never, ctx) as Promise<EvalResult>,
  });

/**
 * Resolve an eager op/verb call's args (positional + kwargs) in parallel,
 * aggregate any errors, then schema-validate the assembled shape. Returns
 * the parsed data on success; throws an `ExprError` (single leaf or
 * `AggregateExprError`) on failure. Shared between `evalOpCall` and
 * `evalVerbCall` (both routed through `dispatchCall`) so eager arg
 * semantics live in one place.
 */
const resolveEagerArgs = async (
  call: OpCall,
  schema: z.ZodObject,
  env: Env,
  path: string,
  state: EvalState,
): Promise<unknown> => {
  const positional = call.params ?? [];
  const kwargEntries = Object.entries(call).filter(([k]) => k !== 'name' && k !== 'params');

  const positionalSettled = await Promise.allSettled(
    positional.map((arg, i) => evalNode(arg, env, paramPath(path, i), state).then((r) => r.value)),
  );
  const kwargsSettled = await Promise.allSettled(
    kwargEntries.map(([k, v]) =>
      evalNode(v as AstInput, env, kwargPath(path, k), state).then((r) => [k, r.value] as const),
    ),
  );

  const { ok: paramValues, errors: paramErrors } = partitionSettled(positionalSettled);
  const { ok: kwargValues, errors: kwargErrors } = partitionSettled(kwargsSettled);
  const errors = [...paramErrors, ...kwargErrors];
  if (errors.length > 0) {
    throwErrors(errors);
  }

  const resolved: Record<string, unknown> = {
    name: call.name,
    params: paramValues,
    ...Object.fromEntries(kwargValues),
  };
  const parsed = parseOpCall(schema, resolved);
  if (!parsed.success) {
    throwErrors(zodIssuesToErrors(parsed.error, path, state.spans));
  }
  return parsed.data;
};

/**
 * Dispatch a pipeline-verb call inside `transform`. Three-branch wrapper
 * (Phase 4-prep sub-task 4-prep.3) over the underlying verb registry:
 *
 *   1. **Lazy** — input dataset is a `LazyDataset`: append a `PlanNode`
 *      to the plan (capturing the active `liftColumn` so a deferred
 *      per-cell verb re-enters its per-cell form at materialise time),
 *      propagate the schema once via `runPropagateSchema`, return a new
 *      `LazyDataset`. Verbs are never invoked; their `apply` runs later,
 *      at materialisation time (4-prep.4).
 *   2. **Per-cell** — input is `MaterializedDataset` and at least one
 *      arg ref (from `meta.referencedColumns`) points at a column whose
 *      `ColumnType` is `nested-dataset`. Each cell is itself a `Dataset`;
 *      we apply the verb to each cell via a recursive `evalVerbCall`
 *      call (lazy cells stay lazy, materialised cells execute). The
 *      outer column's schema entry rewrites to `nested-dataset` wrapping
 *      the verb's `propagateSchema` output against the inner schema.
 *   3. **Materialised, no lift** — standard arquero execution via
 *      `dispatchCall`. Eager verbs share `resolveEagerArgs` with
 *      top-level eager ops; special verbs (filter, derive) receive raw
 *      AST plus a `compile` callback that walks expression-context
 *      children via `runtime/arquero.compileExpr`.
 *
 * After the verb's `apply` returns (branches 2 and 3 only), the engine
 * assigns the propagated `Schema` from the verb's `propagateSchema`
 * (§5.5). Verbs that omit `propagateSchema` keep `apply`'s synthesised
 * all-`unknown` schema — that fallback comes from `withTable`. When such
 * a verb (a runtime-width verb like `spread`/`pivot`, whose output
 * columns are data-dependent) is hit on a lazy input, Branch 1 cannot
 * construct a precise lazy `Schema`, so it force-materialises the input
 * and re-dispatches — `apply` then builds the schema on concrete data.
 */
export const evalVerbCall = async (
  call: OpCall,
  dataset: Dataset,
  env: Env,
  path: string,
  state: EvalState,
): Promise<Dataset> => {
  // Resolve verb meta once for all three branches. `dispatchCall` does
  // its own lookup on the materialised path — a double-lookup we accept
  // for now to keep branches 1 and 2 above the dispatcher. The op
  // surface is small enough that the extra `Map.get` is negligible.
  const { meta } = resolveOpCall(
    call.name,
    state.verbs,
    pipelineVerbs,
    opCallPos(state.spans, path),
    'pipeline verb',
    UnknownVerbError,
  );
  const verbMeta = meta as VerbMeta;

  // Branch (1): Lazy — append a plan node, defer execution.
  if (isLazy(dataset)) {
    const newSchema = runPropagateSchema(
      verbMeta,
      call,
      dataset.schema,
      state.ops,
      state.spans,
      path,
      state.liftColumn,
    );
    if (newSchema === undefined) {
      // Runtime-width verbs (spread / pivot) cannot declare a precise
      // `propagateSchema` — their output column set is data-dependent
      // and `materialize`'s `schemasEqual` post-condition demands an
      // exact schema. Rather than dead-ending a lazy chain (the qkg
      // path is always lazy) with an opaque internal error, force the
      // input to materialise here and re-dispatch: `apply` then builds
      // the real schema on concrete data. This is the sanctioned lazy
      // fall-back for verbs that legitimately omit `propagateSchema`.
      const forced = await materialize(dataset, state.evalCtx, state.ops, state.verbs);
      return evalVerbCall(call, forced, env, path, state);
    }
    return new LazyDataset(
      {
        source: dataset.plan.source,
        ops: [
          ...dataset.plan.ops,
          { verb: call.name, args: call, schemaAfter: newSchema, liftColumn: state.liftColumn },
        ],
      },
      newSchema,
    );
  }

  // Narrow to materialised — required by both per-cell lift and the
  // standard arquero execution path below.
  const materialized = expectMaterialized(dataset, '(transform <Dataset> <verb> ...)', opCallPos(state.spans, path));

  // Shared cross-dataset set-reference seam. For `filter`, resolve any
  // `(col other-ds …)` references inside the predicate to inline
  // literal vectors *now* — at plan-replay / materialize time, just
  // before the predicate is compiled. This makes cross-dataset
  // set-refs work in the s-expr `transform` tool's client-side filter
  // WITHOUT breaking transform laziness (lazy inputs took branch 1
  // above and never reach here until materialised). It is a genuine
  // no-op for an already-literalised qkg residual predicate — there is
  // no `(col …)` left for the walk to rewrite. `materialize` /
  // `evalNode` are bound to this script's `env` / registries.
  const effectiveCall =
    call.name === 'filter' && call.params?.length === 1
      ? {
          ...call,
          params: [
            await resolveSetRefsInPredicate(call.params[0] as AstInput, {
              evalNode: (n) => evalNode(n, env, path, state),
              session: state.evalCtx.session,
              materialize: (ds) => materialize(ds, state.evalCtx, state.ops, state.verbs),
              pos: opCallPos(state.spans, path),
              // A `(col …)` is left for `compileExpr` (per-row accessor,
              // NOT a cross-dataset set-ref) when its first label is
              // either:
              //   - NOT a session binding — a struct-sub-path accessor
              //     on the current row (e.g. `(= (col wellString
              //     externalId) "x")`); or
              //   - the enclosing transform's OWN source binding — a
              //     self-reference (e.g. `(transform foo (filter (= (col
              //     foo wellString externalId) (col other externalId))))`).
              //     Without this, `foo` being a live binding would make
              //     the resolver capture the whole column as a constant
              //     1000-cap vector instead of a per-row value — the
              //     silent-0-rows footgun. Only a `(col <other-bound-ds>
              //     …)` is a genuine foreign set-ref.
              skipNode: (n) => {
                const labels = colNodeLabels(n);
                if (labels === undefined || labels.length === 0) {
                  return false;
                }
                const head = labels[0] as string;
                return head === state.sourceBindingName || state.evalCtx.session.getBinding(head) === undefined;
              },
            }),
          ],
        }
      : call;

  // Branch (2): Per-cell — a verb-arg ref into a `nested-dataset`
  // column lifts the verb's apply onto each cell. Type-driven dispatch
  // via the verb's `referencedColumns` hook; recursion through
  // `evalVerbCall` lets lazy cells stay lazy.
  const refs = (verbMeta.referencedColumns?.(effectiveCall as never, path) ?? []) as readonly ColumnRefInArg[];
  // Multi-axis (PHASE_4_PREP.md D11.1): lift over EVERY referenced
  // `nested-dataset` column, not just the first. De-duped, source
  // order preserved. Single-axis is the n=1 case — behaviour
  // unchanged from the original `.find` selector. `colLabels` refs are
  // explicitly excluded: a `(col …)` in an expression body is a
  // per-row accessor on the current row, never a per-cell lift axis
  // (its `name` is a rendered tuple that wouldn't match a schema key
  // anyway — the guard makes the intent explicit and future-proof).
  const liftAxes = [
    ...new Set(
      refs
        .filter((r) => r.colLabels === undefined && materialized.schema.get(r.name)?.kind === 'nested-dataset')
        .map((r) => r.name),
    ),
  ];
  if (liftAxes.length > 0) {
    return liftPerCell(effectiveCall, materialized, liftAxes, verbMeta, env, path, state);
  }

  // Branch (3): Materialised, no lift — standard arquero execution.
  const wrapPropagated = (parsed: unknown, result: MaterializedDataset): MaterializedDataset => {
    const propagated = runPropagateSchema(
      verbMeta,
      parsed,
      materialized.schema,
      state.ops,
      state.spans,
      path,
      state.liftColumn,
    );
    return propagated === undefined ? result : new MaterializedDataset(result.columns, propagated);
  };
  return dispatchCall<VerbMeta, Dataset, SpecialVerbCtx>(effectiveCall, env, path, state, {
    table: state.verbs,
    registry: pipelineVerbs,
    metaLabel: 'pipeline verb',
    notFound: UnknownVerbError,
    buildSpecialCtx: () => ({
      spans: state.spans,
      path,
      evalCtx: state.evalCtx,
      ops: state.ops,
      verbs: state.verbs,
      liftColumn: state.liftColumn,
      env,
      // Shared with the top-level special-op factory in `evalOpCall`:
      // the `filter` verb's plan-replay seam resolves cross-dataset
      // set references via the shared resolver, which needs to
      // materialise the referenced column.
      materialize: (ds) => materialize(ds, state.evalCtx, state.ops, state.verbs),
      compile: (node, p) => compileExpr(node as AstInput, state.ops, state.spans, p),
    }),
    // See evalOpCall for the `as never` rationale; verb apply returns
    // narrow back to `MaterializedDataset` via the author's annotation.
    callEager: async (m, parsed) =>
      wrapPropagated(parsed, (await m.apply(parsed as never, materialized)) as MaterializedDataset),
    callSpecial: async (m, data, ctx) =>
      wrapPropagated(data, (await m.apply(data as never, materialized, ctx)) as MaterializedDataset),
  });
};
/**
 * Per-cell lift helper (Phase 4-prep 4-prep.3; multi-axis per
 * PHASE_4_PREP.md D11.1). Called by `evalVerbCall` when a verb's
 * `referencedColumns` reports refs into one OR MORE `nested-dataset`
 * columns on the input dataset. For EACH such column the verb's
 * `apply` is re-entered per cell-`Dataset` independently (via a
 * recursive `evalVerbCall`, so lazy cells stay lazy and the verb is
 * never handed a `LazyDataset`); the outer `MaterializedDataset` is
 * then rebuilt with, for every lifted column:
 *
 *   - `columns[col]` replaced by that column's per-cell results.
 *   - `schema[col]` rewritten to `nested-dataset` wrapping the verb's
 *     `propagateSchema` output against the column's inner schema,
 *     computed with `liftColumn = col` so the verb can name per-axis
 *     outputs (e.g. `time-bucket` emits `<col>-<agg>`).
 *
 * Single-axis is the n=1 case — byte-identical to the original
 * single-column behaviour. Cross-axis bucket-grid alignment (the
 * union / null-fill of D11.1) is NOT performed here: it is realised
 * by `time-bucket`'s deterministic `:window` grid (every axis emits
 * the same keys) and finalised by multi-column `unnest`'s zip
 * (D11.3) — the only consumer that needs the axes positionally
 * aligned and the only place a cell is legitimately materialised.
 * Keeping this helper materialisation-free preserves the substrate
 * invariant "lazy cells stay lazy through the recursion".
 *
 * Non-lifted columns pass through unchanged. The inner-schema-after
 * is the single source of truth for each lifted column's shape — not
 * derived from produced cells (a cell may be a `LazyDataset` whose
 * schema is already final), so zero-row outer datasets still rewrite
 * correctly.
 */
const liftPerCell = async (
  call: OpCall,
  dataset: MaterializedDataset,
  columnNames: readonly string[],
  verbMeta: VerbMeta,
  env: Env,
  path: string,
  state: EvalState,
): Promise<MaterializedDataset> => {
  // Resolve each axis once: its cells, its inner-schema-after (SSOT
  // for the rewritten outer type, computed BEFORE iterating cells so
  // it is uniform even for zero-row outer datasets), and its per-cell
  // results from a recursive dispatch carrying `liftColumn = col`.
  const lifted = await Promise.all(
    columnNames.map(async (columnName) => {
      const cells = dataset.columns[columnName];
      const colType = dataset.schema.get(columnName);
      if (cells === undefined || colType?.kind !== 'nested-dataset') {
        // Dispatch invariant — `evalVerbCall` only selects axes whose
        // schema entry is nested-dataset. Defensive re-check.
        throw new OpExecutionError(
          call.name,
          `per-cell lift expected column \`${columnName}\` to be nested-dataset`,
          opCallPos(state.spans, path),
        );
      }
      const innerSchemaAfter =
        runPropagateSchema(verbMeta, call, colType.innerSchema, state.ops, state.spans, path, columnName) ??
        colType.innerSchema;
      const axisState: EvalState = { ...state, liftColumn: columnName };
      const newCells = await Promise.all(
        cells.map((cell, i) => {
          if (!(cell instanceof Dataset)) {
            throw new OpExecutionError(
              call.name,
              `cell at \`${columnName}\`[${i}] is not a Dataset (nested-dataset column invariant violated)`,
              opCallPos(state.spans, path),
            );
          }
          // Recursive dispatch — lazy cells re-enter branch (1) and
          // stay lazy; materialised cells re-enter branch (3).
          return evalVerbCall(call, cell, env, path, axisState);
        }),
      );
      return [columnName, { innerSchemaAfter, newCells }] as const;
    }),
  );
  const liftedByName = new Map(lifted);

  // Rebuild the outer schema: every lifted column's type rewrites to
  // nested-dataset(<inner-after>); insertion order preserved.
  const newOuterSchema = schemaFromEntries(
    Array.from(dataset.schema.entries()).map(([k, t]) => {
      const hit = liftedByName.get(k);
      return (hit === undefined ? [k, t] : [k, nestedDatasetCol(hit.innerSchemaAfter)]) as readonly [string, typeof t];
    }),
  );
  const newColumns = { ...dataset.columns };
  for (const [name, { newCells }] of liftedByName) {
    newColumns[name] = newCells;
  }
  return new MaterializedDataset(newColumns, newOuterSchema);
};

/**
 * Collect `Promise.allSettled` results into successes + flat error list.
 * Spec §9.3: nested aggregates are flattened so callers see a single flat
 * list of leaf errors regardless of how many branches errored.
 */
const partitionSettled = <T>(settled: readonly PromiseSettledResult<T>[]): { ok: T[]; errors: ExprError[] } => {
  const ok: T[] = [];
  const errors: ExprError[] = [];
  settled.forEach((r) => {
    if (r.status === 'fulfilled') {
      ok.push(r.value);
      return;
    }
    errors.push(...errorsFrom(r.reason));
  });
  return { ok, errors };
};

/**
 * Normalise a caught throwable to a flat list of `ExprError` leaves.
 * - `AggregateExprError` → flattened children
 * - `ExprError` → `[e]`
 * - anything else (rare — almost all throws are ExprError) → wrapped as a
 *   sentinel `OpExecutionError` so the partition can continue.
 */
const errorsFrom = (e: unknown): readonly ExprError[] => {
  if (e instanceof AggregateExprError) {
    return e.flatten();
  }
  if (e instanceof ExprError) {
    return [e];
  }
  return [new OpExecutionError('<unknown>', e instanceof Error ? e.message : String(e), NO_POS)];
};

const zodIssuesToErrors = (err: z.ZodError, basePath: string, spans: Spans): readonly ExprError[] =>
  err.issues.map((issue) => {
    const fullPath = joinPath(basePath, issue.path.map(String));
    const pos = spans.get(fullPath) ?? opCallPos(spans, basePath);
    return new ShapeError(issue.message, pos, argRefFromZodPath(issue.path));
  });

/**
 * Throw the collected `errors` as either the single leaf (when one) or an
 * `AggregateExprError` (when many). Centralises the throw shape so callers
 * don't repeat the length check. Precondition: `errors.length > 0`.
 */
const throwErrors = (errors: readonly ExprError[]): never => {
  if (errors.length === 1) {
    const [first] = errors;
    if (first === undefined) {
      throw new Error('throwErrors: empty errors list');
    }
    throw first;
  }
  throw new AggregateExprError(errors);
};
